/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { MutableRefObject } from "react";

import { useMutation } from "@tanstack/react-query";
import cn from "classnames";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import VerificationInput from "react-verification-input";

import { confirm2FACodeLogin, skipOTP } from "lib/v3/loginAPI";
import { useAuth } from "state";

import { GoBackIcon } from "components/icons";
import AttentionIcon from "components/icons/AttentionIcon";
import QuestionMarkIcon from "components/icons/QuestionMarkIcon";
import Text from "components/ui/Text";
import { Button } from "components/ui/Button";
import Stack from "components/ui/Stack";
import Tooltip from "components/ui/NewTooltip/Tooltip";
import { LoginResponse } from "pages/Login/types/login.types";

import styles from "../../style.module.css";

import { useEnvs } from "state/EnvironmentsContext/EnvContext";

type FormData = {
  verificationCode: string;
};

interface FAVerificationProps {
  setCurrentForm: (formName: string) => void;
  loginDataRef: MutableRefObject<LoginResponse | null>;
}

const FAVerification = ({ setCurrentForm }: FAVerificationProps) => {
  const { handleLogin } = useAuth();

  const environment = useEnvs((state) => state.environment);
  const isDevelopment = environment === "development";
  const isStaging = environment === "staging";

  const {
    control,
    watch,
    getValues,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      verificationCode: "",
    },
  });

  const verifyCode = useMutation(confirm2FACodeLogin);
  const verificationCode = watch("verificationCode");
  const confirmationLength = 6;
  const isCompleted = verificationCode?.length === confirmationLength;

  const onSubmit: SubmitHandler<FormData> = () => {
    const code = getValues("verificationCode");
    verifyCode.mutate(code, {
      onSuccess: ({ data }) => {
        clearErrors("verificationCode");
        const { token } = data;
        localStorage.setItem("token", token);
        handleLogin(data);
      },
      onError: () => {
        setError("verificationCode", {
          type: "manual",
          message: "Code is incorrect. Please try again.",
        });
      },
    });
  };

  const handleComplete = () => {
    const verificationCode = getValues("verificationCode");
    if (verificationCode.length === confirmationLength) {
      handleSubmit(onSubmit)();
    }
  };

  const handleChange = (value: string) => {
    if (/^\d*$/.test(value)) {
      clearErrors("verificationCode");
      setValue("verificationCode", value);
    }
  };

  const handleBackButton = () => {
    localStorage.clear();
    window.location.reload();
  };

  const handleSkipOTP = async () => {
    const { data } = await skipOTP();
    localStorage.setItem("token", data.token);
    handleLogin(data);
  };

  const isDisabled = !isCompleted || verifyCode.isLoading;

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col w-full max-w-[545px] items-center"
      >
        <Stack className="space-y-10">
          <Stack className="space-y-8">
            <Stack className="gap-1">
              <div className="flex items-center gap-2">
                <button type="button" onClick={handleBackButton}>
                  <GoBackIcon fill="#fff" width="20" height="20" />
                </button>
                <h3 className="text-white text-lg md:text-xl font-bold">
                  Two-Factor Authentication
                </h3>
              </div>

              <Text className="text-sm text-[#9A9A9A]">
                Enter the code generated by two-factor authenticator app.
              </Text>
            </Stack>

            <div className="flex flex-col items-center justify-center gap-2">
              <Controller
                name="verificationCode"
                control={control}
                render={({ field }) => (
                  <VerificationInput
                    {...field}
                    placeholder="-"
                    length={confirmationLength}
                    onChange={(value) => handleChange(value)}
                    onComplete={handleComplete}
                    classNames={{
                      container: "w-[260px] md:w-[300px]",
                      characterSelected: cn(
                        "border border-solid bg-transparent text-s align-middle text-center outline-none",
                        errors.verificationCode
                          ? "border-[#E46D64] text-white"
                          : "border-[#535353] text-white",
                      ),
                      characterInactive: cn(
                        "border border-solid bg-transparent text-s align-middle text-center",
                        errors.verificationCode
                          ? "border-[#E46D64] text-[#E46D64]"
                          : "border-[#535353] text-[#E46D64]",
                      ),
                      characterFilled: cn(
                        "border border-solid bg-transparent align-middle text-center",
                        errors.verificationCode
                          ? "border-[#E46D64] text-white"
                          : "border-[#535353] text-white",
                      ),
                      character: cn(
                        "bg-transparent border border-solid",
                        errors.verificationCode
                          ? "border-[#E46D64] text-white"
                          : "border-[#535353] text-white",
                      ),
                    }}
                  />
                )}
              />

              {errors.verificationCode && (
                <Text className="w-full flex items-center justify-center gap-1 text-[#E46D64] text-xs text-center">
                  <AttentionIcon fill="#E46D64" />
                  {errors.verificationCode.message}
                </Text>
              )}
            </div>
          </Stack>

          <div className="flex flex-col items-center space-y-5 mt-10">
            <Button
              variant="primary"
              type="submit"
              size="xl"
              className={cn({
                [styles.disabled]: isDisabled,
              })}
              isLoading={verifyCode.isLoading}
              disabled={isDisabled}
              shouldPreventDefault={false}
              id="VerifyBtn"
            >
              Verify
            </Button>

            {/* Replace Hidden with Flex when you decide to use it */}

            <div className="items-center space-x-2 hidden">
              <Button
                variant="unstyled"
                className="text-xs font-bold text-white underline"
                onClick={() => setCurrentForm("backup_code_auth")}
              >
                Use a Backup Code
              </Button>

              <Tooltip
                customIcon={<QuestionMarkIcon />}
                tooltipClassName="w-80 !p-3 !bg-[#3C3C3C]"
                arrowColor="#3C3C3C"
              >
                <p className="text-xs text-white">
                  If you're unable to access your authenticator app, you can use
                  your backup code to authenticate. Backup codes are provided
                  during the setup of two-factor authentication and serve as an
                  alternative method for logging in securely.
                </p>
              </Tooltip>
            </div>
          </div>
        </Stack>
      </form>

      {(isDevelopment || isStaging) && (
        <Button
          variant="ghost"
          className="w-max p-2 rounded-md text-lg font-bold text-white mt-5"
          onClick={() => {
            handleSkipOTP();
          }}
        >
          Skip 2FA
        </Button>
      )}
    </>
  );
};

export default FAVerification;
